import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListView } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    ImportRow,
    ImportRowEditHandler,
    ImportRowExclusionHandlers,
} from '../../importRows';
import { ImportCard } from './ImportCard';
import { IMPORT_CARD_ESTIMATED_HEIGHT_PX } from './importCardsMeta';

type ImportCardRowsProps = ImportRowExclusionHandlers &
    ImportRowEditHandler & {
        rows: ImportRow[];
        emptyMessage: string;
        /** A card's press; stable for the list's lifetime. */
        onAction: (key: React.Key) => void;
    };

/**
 * The virtualized `ListView` of `ImportCardList`, apart so it renders only
 * when its rows, its words or its handlers change: like the table's grid, a
 * render walks every item it holds, so the import's running state does not
 * reach it as a prop.
 */
export const ImportCardRows = React.memo<ImportCardRowsProps>(
    function ImportCardRows({
        rows,
        emptyMessage,
        onEditRow,
        onExcludeRow,
        onIncludeRow,
        onAction,
    }) {
        const { t, i18n } = useTranslation('pages');

        return (
            <ListView<ImportRow>
                virtualized
                aria-label={t('catalogImport.products.table.ariaLabel')}
                dependencies={[
                    i18n.language,
                    onEditRow,
                    onExcludeRow,
                    onIncludeRow,
                ]}
                items={rows}
                onAction={onAction}
                renderEmptyState={() => (
                    <span
                        {...getNodeTestIdProp('catalog-import-products-empty')}
                    >
                        {emptyMessage}
                    </span>
                )}
                rowHeight={IMPORT_CARD_ESTIMATED_HEIGHT_PX}
            >
                {(row) => (
                    <ImportCard
                        id={row.rowNumber}
                        onEditRow={onEditRow}
                        onExcludeRow={onExcludeRow}
                        onIncludeRow={onIncludeRow}
                        row={row}
                    />
                )}
            </ListView>
        );
    },
);
